import { resolveFormClusters } from "./clustering";
import { excludeClusterableNodes, excludeFields, excludeForms } from "./exclusion";
import { isProcessableField, selectInputCandidates } from "./field";
import { isProcessed, removeClassifierFlags, removeHiddenFlag } from "./flags";
import { selectFormCandidates } from "./form";
import { getOverridableFields, getOverridableForms } from "./overrides";
import { isVisibleForm } from "./visible";

/* Before running the detectors :
 * - flag ignorable forms & fields
 * - resolve form clusters */
export const prepass = (doc: Document = document) => {
  excludeForms(doc);
  excludeClusterableNodes(doc);
  excludeFields(doc);
  resolveFormClusters(doc);
};

/** Determine if the classifier should run based on
 * detection triggers. Classification should happen when:
 * - Processed forms have new processable fields (reset flags and re-run classifier)
 * - Any detected unprocessed form becomes visible
 * - Any detected dangling unprocessed field becomes visible */
export const shouldRunClassifier = (): boolean => {
  const formCandidates = getOverridableForms().concat(selectFormCandidates());

  const shouldRunForForms = formCandidates.reduce<boolean>((runDetection, form) => {
    if (isProcessed(form)) {
      const unprocessedFields = selectInputCandidates(form).some(isProcessableField);
      if (unprocessedFields) removeClassifierFlags(form, { preserveIgnored: false });
      return runDetection || unprocessedFields;
    }

    if (isVisibleForm(form)) {
      removeHiddenFlag(form);
      return true;
    }

    return runDetection;
  }, false);

  if (shouldRunForForms) return true;

  /** If no forms triggered classification, check for dangling visible fields */
  const fieldCandidates = getOverridableFields().concat(selectInputCandidates());
  return fieldCandidates.some(isProcessableField);
};
